import ExtensionOption, { ExtensionOptionType } from "@/types/ExtensionOption.interface"

interface State {
    extensionOptions: ExtensionOption[]
}

const defaults = () => {
    return {
        extensionOptions: []
    }
}

export default {
    state: defaults(),
    mutations: {
        extensionOptionsPush (state: State, extensions: ExtensionOption[]): void {
            state.extensionOptions = window.removeDuplicatesById(state.extensionOptions, extensions)
        },
        extensionOptionsRemove (state: State, ids: number[]): void {
            //  Grab our extension options
            const extensions = state.extensionOptions
            
            //  If no extensions, do nothing
            if (!extensions) return

            //  Else, filter out our ids from our current extensions
            const filtered = extensions.filter((x: ExtensionOption) => !ids.includes(x.id))

            //  Replace our extensions with the filtered
            state.extensionOptions = filtered
        },
        extensionOptionsSet (state: State, extensions: ExtensionOption[]): void {
            state.extensionOptions = extensions
        },
        extensionOptionsReset(state: State): void {
            Object.assign(state, defaults())
        }
    },
    actions: {
        extensionOptionsAll ({ commit, getters }): Promise<ExtensionOption[]> {
            //  Assemble our API endpoint
            const url = `${getters.api}/ext-options`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our extension options
                commit('extensionOptionsSet', response.data)

                //  Set our extension options view
                commit('customFieldsExtensionOptionsSet', response.data)

                //  Return the extension options
                return response.data
            }).catch(() => null)
        },
        extensionOptionsDelete ({ commit, getters }, optionId: number): Promise<ExtensionOption> {
            //  Assemble the API endpoint
            const url = `${getters.api}/ext-options/${optionId}`

            //  Send the request
            return window.axios.delete(url).then(response => {
                //  Remove this from our state
                commit('extensionOptionsRemove', [optionId])

                //  Remove this from our extension options view
                commit('customFieldsExtensionOptionsRemove', [optionId])

                //  Return our deleted extension option
                return response.data
            }).catch(() => null)
        },
        extensionOptionsFind ({ getters }, optionId: number): Promise<ExtensionOption> {
            //  Assemble the API endpoint
            const url = `${getters.api}/ext-options/${optionId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Return our found extension option
                return response.data
            }).catch(() => null)
        },
        extensionOptionsPost ({ commit, getters }, option: ExtensionOption): Promise<ExtensionOption> {
            //  Assemble the API endpoint
            const url = `${getters.api}/ext-options`

            //  Send the request
            return window.axios.post(url, option).then(response => {
                //  Add this to our state
                commit('extensionOptionsPush', [response.data])

                //  Add this to our extension options view
                commit('customFieldsExtensionOptionsPush', [response.data])

                //  Return the newly created extension option
                return response.data
            }).catch(() => null)
        },
        extensionOptionsPut ({ commit, getters }, option: ExtensionOption): Promise<ExtensionOption> {
            //  Assemble the API endpoint
            const url = `${getters.api}/ext-options`

            //  Send the request
            return window.axios.put(url, option).then(response => {
                //  Update our state
                commit('extensionOptionsPush', [response.data])

                //  Update our extension options view
                commit('customFieldsExtensionOptionsPush', [response.data])

                //  Return the updated extension option
                return response.data
            }).catch(() => null)
        }
    },
    getters: {
        extensionOptions (state: State): ExtensionOption[] {
            return state.extensionOptions
        },
        extensionOptionTypes (): ExtensionOptionType[] {
            return [
                ExtensionOptionType.text,
                ExtensionOptionType.number,
                ExtensionOptionType.dropdown,
                ExtensionOptionType.date,
                ExtensionOptionType.phone
            ]
        },
        newExtensionOption (): ExtensionOption {
            return {
                name: null,
                type: null,
                data: null
            }
        },
        getExtension: (state: State) => (name: string): ExtensionOption => {
            return state.extensionOptions?.find((ext: ExtensionOption) => {
                return name === ext.name
            })
        }
    }
}